import { Box, Select, TextInput } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { TFunction } from "react-i18next";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { clientConfigAtom } from "atoms/clientConfig";
import {
  otherDriverFirstNameAtom,
  otherDriverLastNameAtom,
  otherDriverPersonalCodeAtom,
  otherVehicleInsuranceAtom,
  otherVehicleLicensePlateAtom,
  otherVehicleTypeAtom,
} from "atoms/otherVehicleInformation";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { VehicleType } from "types/DigitalJourneySession";
import { getInsurances } from "utils/digitalJourney/insurances";

export const vehicleTypeOptions = (t: TFunction) => [
  {
    label: t("digital_journey.select_vehicle.vehicle_type_car"),
    value: VehicleType.CAR,
  },
  {
    label: t("digital_journey.select_vehicle.vehicle_type_moto"),
    value: VehicleType.MOTORCYCLE,
  },
];

export const DJOtherDriverDetailsPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [otherVehicleLicensePlate, setOtherVehicleLicensePlate] = useAtom(otherVehicleLicensePlateAtom);
  const [otherVehicleType, setOtherVehicleType] = useAtom(otherVehicleTypeAtom);
  const [otherVehicleInsurance, setOtherVehicleInsurance] = useAtom(otherVehicleInsuranceAtom);
  const [otherDriverFirstName, setOtherDriverFirstName] = useAtom(otherDriverFirstNameAtom);
  const [otherDriverLastName, setOtherDriverLastName] = useAtom(otherDriverLastNameAtom);
  const [otherDriverPersonalCode, setOtherDriverPersonalCode] = useAtom(otherDriverPersonalCodeAtom);
  const { clientId } = useAtomValue(clientConfigAtom);
  const insurances = getInsurances(clientId);

  const fiscalCodeError = otherDriverPersonalCode && otherDriverPersonalCode.length !== 16
    ? "Il codice fiscale deve essere di 16 caratteri"
    : undefined;
  const isDisabled = !otherVehicleLicensePlate || !otherVehicleType || !!fiscalCodeError;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Other Driver Details - continue" });
    onNext();
  };

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          Informazioni sull'altro veicolo
        </Text>
        <TextInput
          name="otherVehicleLicensePlate"
          value={otherVehicleLicensePlate}
          onChange={(e) => setOtherVehicleLicensePlate(e.target.value)}
          label={
            <Text weight={700} sizeVariant="md">
              Targa dell'altro veicolo*
            </Text>
          }
          maxLength={8}
        />
        <Box>
          <Text weight={700} sizeVariant="md" mb={4}>
            L'altro veicolo è*:
          </Text>
          <Flex gap={16} direction="row" role="radiogroup" aria-labelledby="otherVehicleType">
            {vehicleTypeOptions(t).map((option) => (
              <Button
                aria-labelledby={`otherVehicleType-${option.label}-label`}
                aria-checked={otherVehicleType === option.value}
                role="radio"
                key={option.label}
                onClick={() => setOtherVehicleType(option.value)}
                typeVariant={otherVehicleType === option.value ? "primary" : "secondary"}
              >
                <label id={`otherVehicleType-${option.label}-label`}>{option.label}</label>
              </Button>
            ))}
          </Flex>
        </Box>
        <Select
          label={
            <>
              <Text as="span" weight={700} sizeVariant="md">
                Compagnia assicurativa dell'altro veicolo
              </Text>{" "}
            </>
          }
          placeholder={t("digital_journey.accident_location.placeholder")}
          data={(insurances || []).map((insurance) => ({ value: insurance, label: insurance }))}
          value={otherVehicleInsurance}
          onChange={(value) => setOtherVehicleInsurance(value || "")}
        />
        <TextInput
          name="otherDriverFirstName"
          value={otherDriverFirstName}
          onChange={(e) => setOtherDriverFirstName(e.target.value)}
          label={
            <>
              <Text as="span" weight={700} sizeVariant="md">
                Nome del conducente dell'altro veicolo
              </Text>{" "}
            </>
          }
        />
        <TextInput
          name="otherDriverLastName"
          value={otherDriverLastName}
          onChange={(e) => setOtherDriverLastName(e.target.value)}
          label={
            <>
              <Text as="span" weight={700} sizeVariant="md">
                Cognome del conducente dell'altro veicolo
              </Text>{" "}
            </>
          }
        />
        <TextInput
          name="otherDriverPersonalCode"
          value={otherDriverPersonalCode}
          onChange={(e) => setOtherDriverPersonalCode(e.target.value)}
          label={
            <>
              <Text as="span" weight={700} sizeVariant="md">
                Codice Fiscale del conducente dell'altro veicolo
              </Text>{" "}
            </>
          }
          error={fiscalCodeError}
          maxLength={16}
        />
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};
